import React, { useState, useEffect } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './styles.scss';

const Calendar = ({ selectedDate, onDateChange, presets = false, rangePresets = null }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);

  // Reflect the already-applied filter value: preselect the range and jump to its month.
  useEffect(() => {
    if (selectedDate?.start && selectedDate?.end) {
      const s = new Date(selectedDate.start);
      const e = new Date(selectedDate.end);
      setStartDate(s);
      setEndDate(e);
      setCurrentDate(new Date(s.getFullYear(), s.getMonth(), 1));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [selectedDate]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    if (!startDate || (startDate && endDate)) {
      // Start new range
      setStartDate(clickedDate);
      setEndDate(null);
    } else {
      // Complete the range
      if (clickedDate < startDate) {
        setEndDate(startDate);
        setStartDate(clickedDate);
      } else {
        setEndDate(clickedDate);
      }
    }
  };

  const handleSetToAny = () => {
    setStartDate(null);
    setEndDate(null);
    if (onDateChange) {
      onDateChange(null);
    }
  };

  const formatDate = (date) => {
  if (!date) return '';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};
const handleAllDay = () => {
  const today = new Date();

  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const end = new Date(start);

  setStartDate(start);
  setEndDate(end);

  if (onDateChange) {
    onDateChange({
      start,
      end,
      label: 'All Day',
    });
  }
};
  const handleDone = () => {
    if (startDate && endDate && onDateChange) {
      onDateChange({ start: startDate, end: endDate });
    }
  };

  // Follow-Up quick presets — send a labelled value that the filter row shows verbatim.
  const applyPreset = (label) => {
    if (!onDateChange) return;
    if (label === 'Any' || label === 'No Value set') {
      setStartDate(null);
      setEndDate(null);
      onDateChange(label === 'Any' ? null : { start: null, end: null, label: 'No Value set' });
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let range = { start: null, end: null };
    if (label === 'Today') {
      range = { start: new Date(today), end: new Date(today) };
    } else if (label === 'Upcoming') {
      const end = new Date(today); end.setFullYear(end.getFullYear() + 1);
      range = { start: new Date(today), end };
    } else if (label === 'Outstanding') {
      const start = new Date(today); start.setFullYear(start.getFullYear() - 1);
      const end = new Date(today); end.setDate(end.getDate() - 1);
      range = { start, end };
    }
    // 'Someday' → no concrete range, just the label.
    if (range.start) { setStartDate(range.start); setEndDate(range.end); setCurrentDate(new Date(range.start.getFullYear(), range.start.getMonth(), 1)); }
    onDateChange({ ...range, label });
  };

  const presetOptions = ['Today', 'Upcoming', 'Outstanding', 'Someday', 'No Value set'];

  // Custom range presets (e.g. Last 7 days / Last 30 days) — set an actual date range.
  const applyRangePreset = (p) => {
    const [s, e] = p.getRange();
    setStartDate(s);
    setEndDate(e);
    setCurrentDate(new Date(s.getFullYear(), s.getMonth(), 1));
    if (onDateChange) onDateChange({ start: s, end: e, label: p.label });
  };

  const isInRange = (day) => {
    if (!startDate) return false;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    if (endDate) {
      return date >= startDate && date <= endDate;
    } else if (hoveredDate) {
      const rangeStart = startDate < hoveredDate ? startDate : hoveredDate;
      const rangeEnd = startDate > hoveredDate ? startDate : hoveredDate;
      return date >= rangeStart && date <= rangeEnd;
    }
    return false;
  };

  const isStartOrEnd = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return (startDate && date.getTime() === startDate.getTime()) || 
           (endDate && date.getTime() === endDate.getTime());
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const inRange = isInRange(day);
    const isStartEnd = isStartOrEnd(day);

    days.push(
      <div
        key={day}
        className={`calendar-day ${inRange ? 'in-range' : ''} ${isStartEnd ? 'selected' : ''}`}
        onClick={() => handleDateClick(day)}
        onMouseEnter={() => setHoveredDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
        onMouseLeave={() => setHoveredDate(null)}
      >
        {day}
      </div>
    );
  }

  return (
    <div className='calender-con'>
    <div className="calendar-container">
      <div className="calendar-header">
        <LeftOutlined onClick={handlePrevMonth} className="nav-icon" />
        <h3 className="calendar-title">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <RightOutlined onClick={handleNextMonth} className="nav-icon" />
       
      </div>

      <div className="calendar-weekdays">
        <div className="weekday">S</div>
        <div className="weekday">M</div>
        <div className="weekday">T</div>
        <div className="weekday">W</div>
        <div className="weekday">T</div>
        <div className="weekday">F</div>
        <div className="weekday">S</div>
      </div>

      <div className="calendar-days">
        {days}
      </div>
{(startDate || endDate) && (
  <div className="calendar-selected-range">
    <div className="range-title">
      Selected Range
    </div>

    <div className="range-value">
      {startDate
        ? formatDate(startDate)
        : '--'}
      {'  '}
      -
      {'  '}
      {endDate
        ? formatDate(endDate)
        : '--'}
    </div>
  </div>
)}
     
    </div>
    <div className={`set-any-way ${(presets || rangePresets) ? 'with-presets' : ''}`}>
      <div className="set-any-options">
        <div
    className="calendar-preset-item"
    onClick={handleAllDay}
  >
    All Day
  </div>
        {/* <div className="calendar-set-any" onClick={handleSetToAny}>
          Set to Any
        </div> */}
        {rangePresets
          ? rangePresets.map(p => (
              <div
                key={p.label}
                className="calendar-preset-item"
                onClick={() => applyRangePreset(p)}
              >
                {p.label}
              </div>
            ))
          : presets && presetOptions.map(opt => (
              <div
                key={opt}
                className="calendar-preset-item"
                onClick={() => applyPreset(opt)}
              >
                {opt === 'No Value set' ? <><span className="preset-no-value">∅</span> No Value set</> : opt}
              </div>
            ))}
      </div>
      {(startDate && endDate) && (
        <button className="calendar-done-btn" onClick={handleDone}>
          Done
        </button>
      )}
    </div>
      </div>
  );
};

export default Calendar;
